import { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Globe,
  Clock,
  Calendar,
  Github,
  Linkedin,
  Twitter,
  Slack,
  Building2,
  ExternalLink,
  Pencil,
  Navigation,
  Users,
  PlaneTakeoff,
} from 'lucide-react';
import { useEmployee } from '../contexts/EmployeeContext';
import { useAuth } from '../contexts/AuthContext';
import ProfileMapPin from '../components/ProfileMapPin';
import TeamIcon from '../components/TeamIcon';
import { haversineKm, formatDistanceToHQ } from '../utils/geoUtils';
import { useHQ } from '../contexts/HQContext';
import type { Employee, Pet } from '../types/employee';
import { petSpeciesIcon } from '../utils/petUtils';

const NEIGHBOUR_RADIUS_KM = 900;
const NEIGHBOUR_MAX = 5;

function pickNeighbours(employees: Employee[], employee: Employee): Employee[] {
  const { latitude: lat, longitude: lng } = employee.location.coordinates;
  const nearby = employees.filter(e => {
    if (e.id === employee.id) return false;
    const dist = haversineKm(lat, lng, e.location.coordinates.latitude, e.location.coordinates.longitude);
    return dist <= NEIGHBOUR_RADIUS_KM;
  });
  // Fisher-Yates shuffle, take up to NEIGHBOUR_MAX
  for (let i = nearby.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [nearby[i], nearby[j]] = [nearby[j], nearby[i]];
  }
  return nearby.slice(0, NEIGHBOUR_MAX);
}

const EmployeeProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const { getEmployeeById, employees } = useEmployee();
  const { employeeProfile } = useAuth();
  const { hq } = useHQ();
  const navigate = useNavigate();
  const isOwnProfile = employeeProfile?.slug === id;

  const employee = getEmployeeById(id || '');

  useEffect(() => {
    if (employee) {
      document.title = `${employee.name} - Global Team Directory`;
    }
  }, [employee]);

  if (!employee) {
    return (
      <div className="text-center py-24">
        <h2 className="text-xl font-semibold text-bolt-text mb-3">Employee Not Found</h2>
        <p className="text-sm text-bolt-textMuted mb-8">
          The employee you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/" className="btn btn-primary">
          Back to Directory
        </Link>
      </div>
    );
  }

  const neighbours = useMemo(
    () => (employee ? pickNeighbours(employees, employee) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employee?.id, employees]
  );

  const nearestNeighbour = useMemo(() => {
    if (!employee || employees.length === 0) return null;
    const { latitude: lat, longitude: lng } = employee.location.coordinates;
    let nearest: Employee | null = null;
    let nearestDist = Infinity;
    for (const e of employees) {
      if (e.id === employee.id) continue;
      const dist = haversineKm(lat, lng, e.location.coordinates.latitude, e.location.coordinates.longitude);
      if (dist < nearestDist) { nearestDist = dist; nearest = e; }
    }
    return nearest;
  }, [employee?.id, employees]);

  const today = new Date().toISOString().slice(0, 10);
  const activeTempLoc = (employee.temporaryLocations ?? []).find(
    loc => loc.startDate <= today && loc.endDate >= today &&
      (loc.coordinates.latitude !== 0 || loc.coordinates.longitude !== 0)
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  return (
    <div className="animate-fade-in space-y-4">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-bolt-textMuted hover:text-bolt-text transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to Directory</span>
      </button>

      <div className="card overflow-hidden">
        {/* Hero banner */}
        <div className="h-32 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 relative overflow-hidden">
          {/* subtle grid overlay */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 31px, rgba(255,255,255,0.15) 31px, rgba(255,255,255,0.15) 32px), repeating-linear-gradient(90deg, transparent, transparent 31px, rgba(255,255,255,0.15) 31px, rgba(255,255,255,0.15) 32px)',
            }}
          />
          {/* World map pin — top-right, transparent, white outline */}
          {employee.location?.coordinates && (
            <div className="absolute right-0 top-0 h-full" style={{ width: '55%', opacity: 0.9 }}>
              <ProfileMapPin
                lat={employee.location.coordinates.latitude}
                lon={employee.location.coordinates.longitude}
                tempLat={activeTempLoc?.coordinates.latitude}
                tempLon={activeTempLoc?.coordinates.longitude}
              />
            </div>
          )}
        </div>

        {/* Avatar strip: sits at the boundary between hero and content */}
        <div className="relative px-6 h-12">
          <div className="absolute -top-10 left-6 ring-4 ring-bolt-surface rounded-full shadow-xl">
            <img
              src={employee.avatar}
              alt={employee.name}
              className="w-20 h-20 rounded-full object-cover"
            />
          </div>
        </div>

        {/* Name + actions row */}
        <div className="px-6 pb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-bolt-text">{employee.name}</h1>
              <p className="text-sm text-bolt-textMuted">{employee.role}</p>
            </div>

            <div className="flex items-center gap-2">
              <a href={`mailto:${employee.email}`} className="btn btn-primary text-xs px-3 py-1.5">
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                Email
              </a>
              {employee.phone && (
                <a href={`tel:${employee.phone}`} className="btn btn-secondary text-xs px-3 py-1.5">
                  <Phone className="h-3.5 w-3.5 mr-1.5" />
                  Call
                </a>
              )}
              {isOwnProfile && (
                <Link to="/profile/edit" className="btn btn-secondary text-xs px-3 py-1.5">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit
                </Link>
              )}
            </div>
          </div>

          {/* Meta row */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-bolt-textMuted">
            {employee.socialMedia?.slack && (
              <span className="flex items-center gap-1">
                <Slack className="h-3.5 w-3.5 text-bolt-textSubtle" />
                @{employee.socialMedia.slack}
              </span>
            )}
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-bolt-textSubtle" />
              {[employee.location.city, employee.location.country].filter(Boolean).join(', ')}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-bolt-textSubtle" />
              {employee.location.timezone}
            </span>
            {activeTempLoc && (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-warning-500/10 border border-warning-500/25 text-warning-400 font-medium">
                <Navigation className="h-3 w-3" />
                Currently in {activeTempLoc.city}, {activeTempLoc.country}
                {activeTempLoc.label && ` · ${activeTempLoc.label}`}
              </span>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-bolt-border" />

        {/* Main content */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left: bio, skills, languages */}
          <div className="md:col-span-2 space-y-6">
            {employee.bio && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle mb-3">About</h2>
                <p className="text-sm text-bolt-textMuted leading-relaxed">{employee.bio}</p>
              </section>
            )}

            {employee.skills?.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle mb-3">Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {employee.skills.map((skill, index) => (
                    <span key={index} className="tag">{skill}</span>
                  ))}
                </div>
              </section>
            )}

            {employee.languages?.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle mb-3">Languages</h2>
                <div className="flex flex-wrap gap-2">
                  {employee.languages.map((language, index) => (
                    <span key={index} className="tag-blue">{language}</span>
                  ))}
                </div>
              </section>
            )}

            {(employee.teams ?? []).length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle mb-3">Teams</h2>
                <div className="flex flex-wrap gap-2">
                  {(employee.teams ?? []).map(team => (
                    <Link
                      key={team.id}
                      to={`/team/${team.id}`}
                      className="tag-team hover:opacity-80 transition-opacity"
                    >
                      <TeamIcon team={team} size={12} />
                      {team.name}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {(employee.pets ?? []).length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle mb-3">Pets</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(employee.pets ?? []).map(pet => (
                    <PetCard key={pet.id} pet={pet} />
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right: details sidebar */}
          <div>
            <div className="bg-bolt-bg border border-bolt-border rounded-xl p-4 space-y-4">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle">Details</h2>

              <DetailRow
                icon={<Building2 className="h-3.5 w-3.5" />}
                label="Department"
                value={employee.department}
              />

              {employee.employer && (
                <DetailRow
                  icon={<Globe className="h-3.5 w-3.5" />}
                  label="Employer"
                  value={employee.employer}
                />
              )}

              {employee.startDate && (
                <DetailRow
                  icon={<Calendar className="h-3.5 w-3.5" />}
                  label="Joined"
                  value={formatDate(employee.startDate)}
                />
              )}

              <DetailRow
                icon={<MapPin className="h-3.5 w-3.5" />}
                label="Location"
                value={[employee.location.city, employee.location.country].filter(Boolean).join(', ')}
              />

              {hq && (
                <DetailRow
                  icon={<Building2 className="h-3.5 w-3.5" />}
                  label="Distance to HQ"
                  value={formatDistanceToHQ(
                    haversineKm(
                      employee.location.coordinates.latitude,
                      employee.location.coordinates.longitude,
                      hq.latitude,
                      hq.longitude,
                    ),
                    hq.name,
                  )}
                />
              )}

              {(employee.temporaryLocations ?? []).length > 0 && (
                <div className="pt-2 border-t border-bolt-border space-y-2">
                  <p className="text-xs text-bolt-textSubtle font-medium flex items-center gap-1.5">
                    <Navigation className="h-3 w-3" />
                    Travel
                  </p>
                  {(employee.temporaryLocations ?? []).map(loc => {
                    const isActive = loc.startDate <= today && loc.endDate >= today;
                    return (
                      <div key={loc.id} className="space-y-0.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm text-bolt-textMuted font-medium">
                            {[loc.city, loc.country].filter(Boolean).join(', ')}
                          </p>
                          {isActive && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warning-500/10 text-warning-400 border border-warning-500/20">
                              Now
                            </span>
                          )}
                        </div>
                        {loc.label && <p className="text-xs text-bolt-textSubtle">{loc.label}</p>}
                        <p className="text-xs text-bolt-textSubtle">{loc.startDate} → {loc.endDate}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {employee.newHireDeckUrl && (
                <div className="pt-2 border-t border-bolt-border">
                  <p className="text-xs text-bolt-textSubtle font-medium mb-2">New Hire Deck</p>
                  <a
                    href={employee.newHireDeckUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs font-medium text-primary-400 hover:text-primary-300 transition-colors group"
                  >
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">View presentation</span>
                    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0 ml-auto" />
                  </a>
                </div>
              )}

              {employee.socialMedia && (
                <div className="pt-2 border-t border-bolt-border space-y-2">
                  <p className="text-xs text-bolt-textSubtle font-medium">Social</p>
                  {employee.socialMedia.linkedin && (
                    <SocialLink
                      icon={<Linkedin className="h-3.5 w-3.5" />}
                      href={`//www.linkedin.com/in/${employee.socialMedia.linkedin}`}
                      label={employee.socialMedia.linkedin}
                    />
                  )}
                  {employee.socialMedia.twitter && (
                    <SocialLink
                      icon={<Twitter className="h-3.5 w-3.5" />}
                      href={`//x.com/${employee.socialMedia.twitter}`}
                      label={employee.socialMedia.twitter}
                    />
                  )}
                  {employee.socialMedia.github && (
                    <SocialLink
                      icon={<Github className="h-3.5 w-3.5" />}
                      href={`//github.com/${employee.socialMedia.github}`}
                      label={employee.socialMedia.github}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Neighbours section — outside the main card */}
      {neighbours.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-bolt-textSubtle" />
            <h2 className="text-xs font-semibold uppercase tracking-widest text-bolt-textSubtle">
              Nearby Colleagues
            </h2>
            <span className="flex items-center gap-1 text-xs text-bolt-textSubtle">
              · within <PlaneTakeoff className="h-3 w-3" /> an hour's flight
            </span>
            {nearestNeighbour && (
              <span className="ml-auto text-xs text-bolt-textSubtle">
                Nearest: <span className="text-bolt-textMuted font-medium">{nearestNeighbour.name}</span>
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {neighbours.map(n => (
              <Link
                key={n.id}
                to={`/employee/${n.id}`}
                className="group flex flex-col items-center gap-2 p-3 rounded-xl bg-bolt-surface border border-bolt-border hover:border-primary-500/40 hover:bg-bolt-surfaceHover transition-all text-center"
              >
                <div className="relative">
                  <img
                    src={n.avatar}
                    alt={n.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-bolt-border group-hover:ring-primary-500/40 transition-all"
                  />
                </div>
                <div className="min-w-0 w-full">
                  <p className="text-xs font-semibold text-bolt-text truncate">{n.name}</p>
                  <p className="text-[11px] text-bolt-textSubtle truncate">{n.location.city}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PetCard = ({ pet }: { pet: Pet }) => {
  const SpeciesIcon = petSpeciesIcon(pet.species);
  return (
    <div className="flex gap-3 rounded-xl border border-bolt-border bg-bolt-bg p-3">
      {pet.photoUrl ? (
        <img
          src={pet.photoUrl}
          alt={pet.name}
          className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-bolt-border"
        />
      ) : (
        <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <SpeciesIcon className="h-6 w-6 text-emerald-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-bolt-text">{pet.name}</span>
          <span className="text-xs text-bolt-textSubtle capitalize">{pet.species}</span>
        </div>
        {pet.role && (
          <p className="text-xs text-emerald-400 font-medium mt-0.5">{pet.role}</p>
        )}
        {pet.bio && (
          <p className="text-xs text-bolt-textMuted mt-1 leading-relaxed line-clamp-3">{pet.bio}</p>
        )}
      </div>
    </div>
  );
};

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const DetailRow = ({ icon, label, value }: DetailRowProps) => (
  <div className="flex items-start gap-2.5">
    <div className="mt-0.5 text-bolt-textSubtle flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-bolt-textSubtle">{label}</p>
      <p className="text-sm text-bolt-textMuted font-medium truncate">{value}</p>
    </div>
  </div>
);

interface SocialLinkProps {
  icon: React.ReactNode;
  href: string;
  label: string;
}

const SocialLink = ({ icon, href, label }: SocialLinkProps) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 text-xs text-bolt-textMuted hover:text-primary-300 transition-colors group"
  >
    <span className="text-bolt-textSubtle group-hover:text-primary-400 transition-colors">{icon}</span>
    <span className="truncate">{label}</span>
    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0" />
  </a>
);

export default EmployeeProfilePage;
